import * as React from "react"
import { Link, RouteComponentProps, LinkGetProps } from "@gatsbyjs/reach-router"
import { useMerlinSourceEventByIdQuery } from "./queries.generated"
import * as styles from "./styles.css"
import {
  SupportedCmsVendor,
  dataSourceNames,
} from "../../constants/data-sources"
import { StatusPill } from "./status-pill"
import { TotalTime } from "./total-time"
import { DataSource } from "./data-source"
import { Timestamps } from "./timestamps"
import { Loading } from "gatsby-ui/src/components"
import { MerlinStatus } from "@gatsbyjs/graphql"
import { formattedStatus } from "../../constants/status-details"
import { isSystemVendor } from "../../utils/is-system-vendor"

interface SourceEventDetailsProps extends RouteComponentProps {
  organizationId: string
  siteId: string
  sourceEventId: string
  children: React.ReactNode
}

export function SourceEventDetails({
  organizationId,
  siteId,
  sourceEventId,
  children,
}: SourceEventDetailsProps) {
  const { data, loading, error } = useMerlinSourceEventByIdQuery({
    variables: {
      sourceEventId,
    },
  })

  const merlinSourceEvent = data?.merlinSourceEventById
  const cmsVendor = merlinSourceEvent?.cmsVendor as SupportedCmsVendor
  const knownSource = dataSourceNames?.[cmsVendor]
  const name = knownSource || `Unknown source`
  const status = merlinSourceEvent?.status as MerlinStatus
  const formattedStatusText = formattedStatus?.[status]

  const isSystemEvent = isSystemVendor(cmsVendor)

  if (loading) {
    return <Loading />
  }

  return (
    <>
      <div className={styles.breadcrumbs}>
        <Link to="../../">Content Hub</Link> &nbsp;{`/`}&nbsp; Update from{" "}
        {name}
      </div>

      <div className={styles.detailHeader}>
        <div className={styles.pillSpacer}>
          <StatusPill
            status={status}
            formattedStatusText={formattedStatusText}
          />
        </div>
        <div className={styles.spacer}>
          <DataSource cmsVendor={cmsVendor} name={name} siteId={siteId} />

          <TotalTime
            createdAt={merlinSourceEvent?.createdAt}
            endedAt={merlinSourceEvent?.endedAt}
          />

          <Timestamps
            status={status}
            createdAt={merlinSourceEvent?.createdAt}
            endedAt={merlinSourceEvent?.endedAt}
          />
        </div>
      </div>

      {!isSystemEvent && (
        <div className={styles.pills}>
          <PillLink to="./">Logs</PillLink>
          <PillLink to="raw">Raw Logs</PillLink>
        </div>
      )}

      {children}
    </>
  )
}

const getLinkProps = ({ isCurrent }: LinkGetProps) => {
  return isCurrent ? { className: styles.pillLinkActive } : {}
}

function PillLink(props: any) {
  return <Link {...props} className={styles.pillLink} getProps={getLinkProps} />
}
